import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { FaLock, FaUser, FaKey, FaPlus, FaTrash, FaCheck, FaTimes, FaChartLine, FaUsers, FaRobot, FaSignOutAlt, FaBell, FaPaperPlane } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const NovayarAdmin = () => {
  const { isDark } = useTheme();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [activeTab, setActiveTab] = useState('stats');
  
  const [stats, setStats] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [showAddKeyModal, setShowAddKeyModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState({ key: '', name: '' });
  
  // Notification states
  const [notificationData, setNotificationData] = useState({
    title: '',
    body: '',
    url: '/novayar',
    icon: ''
  });
  const [sendingNotification, setSendingNotification] = useState(false);
  const [pushSubscriptionsCount, setPushSubscriptionsCount] = useState({ active: 0, total: 0 });

  // بررسی لاگین قبلی
  useEffect(() => {
    const savedToken = localStorage.getItem('novayarAdminToken');
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
      loadData(savedToken);
    }
  }, []);

  const loadData = async (accessToken) => {
    loadStats(accessToken);
    loadApiKeys(accessToken);
    loadSubscriptions(accessToken);
    loadPushSubscriptionsCount(accessToken);
  };

  const loadStats = async (accessToken) => {
    try {
      const response = await axios.get(`${API_URL}/api/novayar-admin/stats`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const loadApiKeys = async (accessToken) => {
    try {
      const response = await axios.get(`${API_URL}/api/novayar-admin/api-keys`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error loading API keys:', error);
    }
  };

  const loadSubscriptions = async (accessToken) => {
    try {
      const response = await axios.get(`${API_URL}/api/novayar-admin/subscription-requests`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      setSubscriptions(response.data);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/novayar-admin/login`, {
        username,
        password
      });

      const accessToken = response.data.access_token;
      setToken(accessToken);
      localStorage.setItem('novayarAdminToken', accessToken);
      setIsLoggedIn(true);
      loadData(accessToken);
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'خطا در ورود. لطفاً دوباره تلاش کنید.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('novayarAdminToken');
    setIsLoggedIn(false);
    setToken('');
    setUsername('');
    setPassword('');
  };

  const handleAddKey = async () => {
    if (!newKeyData.key) {
      alert('لطفاً کلید API را وارد کنید');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/novayar-admin/api-keys`,
        newKeyData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setShowAddKeyModal(false);
      setNewKeyData({ key: '', name: '' });
      loadApiKeys(token);
      alert('کلید با موفقیت اضافه شد');
    } catch (error) {
      alert(error.response?.data?.detail || 'خطا در افزودن کلید');
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('آیا از حذف این کلید اطمینان دارید?')) return;

    try {
      await axios.delete(`${API_URL}/api/novayar-admin/api-keys/${keyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadApiKeys(token);
      alert('کلید با موفقیت حذف شد');
    } catch (error) {
      alert('خطا در حذف کلید');
    }
  };

  const handleApproveSubscription = async (requestId) => {
    try {
      await axios.put(
        `${API_URL}/api/novayar-admin/subscription-requests/${requestId}/approve`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      loadSubscriptions(token);
      loadStats(token);
      alert('اشتراک با موفقیت تایید شد');
    } catch (error) {
      alert('خطا در تایید اشتراک');
    }
  };

  const handleRejectSubscription = async (requestId) => {
    try {
      await axios.put(
        `${API_URL}/api/novayar-admin/subscription-requests/${requestId}/reject`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      loadSubscriptions(token);
      alert('درخواست رد شد');
    } catch (error) {
      alert('خطا در رد درخواست');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fa-IR');
  };

  const loadPushSubscriptionsCount = async (accessToken) => {
    try {
      const response = await axios.get(`${API_URL}/api/novayar-admin/push-subscriptions-count`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      setPushSubscriptionsCount(response.data);
    } catch (error) {
      console.error('Error loading push subscriptions count:', error);
    }
  };

  const handleSendNotification = async () => {
    if (!notificationData.title || !notificationData.body) {
      alert('لطفاً عنوان و متن نوتیفیکیشن را وارد کنید');
      return;
    }

    if (!window.confirm(`آیا می‌خواهید این نوتیفیکیشن را به ${pushSubscriptionsCount.active} کاربر فعال ارسال کنید؟`)) {
      return;
    }

    setSendingNotification(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/novayar-admin/send-notification`,
        notificationData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      alert(`✅ نوتیفیکیشن با موفقیت ارسال شد!\n\nارسال شده: ${response.data.sent}\nناموفق: ${response.data.failed}\nکل: ${response.data.total}`);
      
      // پاک کردن فرم
      setNotificationData({
        title: '',
        body: '',
        url: '/novayar',
        icon: ''
      });
      
    } catch (error) {
      alert(error.response?.data?.detail || 'خطا در ارسال نوتیفیکیشن');
    } finally {
      setSendingNotification(false);
    }
  };

  // صفحه لاگین
  if (!isLoggedIn) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${
        isDark ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-blue-900 via-purple-800 to-blue-900'
      }`}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-3xl shadow-2xl overflow-hidden transition-colors duration-300`}>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-center">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                <FaRobot className="text-4xl text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">پنل مدیریت نوآیار</h1>
              <p className="text-white/80">دستیار هوشمند سامانه نوآفرین‌شو</p>
            </div>

            {/* Form */}
            <div className="p-8">
              {loginError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-100 border-2 border-red-500 rounded-xl flex items-center gap-3 text-red-900"
                >
                  <FaTimes className="text-2xl flex-shrink-0" />
                  <span className="font-semibold">{loginError}</span>
                </motion.div>
              )}

              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className={`block mb-2 font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    نام کاربری
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      className={`w-full p-4 pr-12 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-800'
                      }`}
                      placeholder="نام کاربری خود را وارد کنید"
                    />
                    <FaUser className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label className={`block mb-2 font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    رمز عبور
                  </label>
                  <div className="relative">
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className={`w-full p-4 pr-12 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-800'
                      }`}
                      placeholder="رمز عبور خود را وارد کنید"
                    />
                    <FaLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  className={`w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold text-lg shadow-xl transition-all ${
                    loading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-2xl'
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-3">
                      <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                      در حال ورود...
                    </span>
                  ) : (
                    'ورود به پنل'
                  )}
                </motion.button>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // پنل ادمین
  return (
    <div className={`min-h-screen py-20 transition-colors duration-300 ${
      isDark ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100'
    }`}>
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-8 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-3xl shadow-xl p-6 transition-colors duration-300`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center">
                <FaRobot className="text-3xl text-white" />
              </div>
              <div>
                <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  پنل مدیریت نوآیار
                </h1>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  دستیار هوشمند سامانه نوآفرین‌شو
                </p>
              </div>
            </div>
            <motion.button
              onClick={handleLogout}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold shadow-lg transition-all"
            >
              <FaSignOutAlt />
              خروج
            </motion.button>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`mb-8 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl overflow-hidden transition-colors duration-300`}
        >
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('stats')}
              className={`flex-1 py-4 px-6 font-bold transition-all ${
                activeTab === 'stats'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FaChartLine className="inline mr-2" />
              آمار
            </button>
            <button
              onClick={() => setActiveTab('api-keys')}
              className={`flex-1 py-4 px-6 font-bold transition-all ${
                activeTab === 'api-keys'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FaKey className="inline mr-2" />
              API Keys
            </button>
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`flex-1 py-4 px-6 font-bold transition-all ${
                activeTab === 'subscriptions'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FaUsers className="inline mr-2" />
              درخواست‌های اشتراک
              {stats && stats.pending_subscriptions > 0 && (
                <span className="mr-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                  {stats.pending_subscriptions}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`flex-1 py-4 px-6 font-bold transition-all ${
                activeTab === 'notifications'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FaBell className="inline mr-2" />
              ارسال نوتیفیکیشن
              {pushSubscriptionsCount.active > 0 && (
                <span className="mr-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                  {pushSubscriptionsCount.active}
                </span>
              )}
            </button>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {activeTab === 'stats' && stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                icon={<FaRobot className="text-4xl text-blue-600" />}
                title="کل چت‌ها"
                value={stats.total_chats}
                bgColor={isDark ? 'bg-blue-900/20' : 'bg-blue-50'}
                isDark={isDark}
              />
              <StatCard
                icon={<FaUsers className="text-4xl text-green-600" />}
                title="کاربران مهمان"
                value={stats.total_guests}
                bgColor={isDark ? 'bg-green-900/20' : 'bg-green-50'}
                isDark={isDark}
              />
              <StatCard
                icon={<FaCheck className="text-4xl text-purple-600" />}
                title="اشتراک‌های فعال"
                value={stats.active_subscriptions}
                bgColor={isDark ? 'bg-purple-900/20' : 'bg-purple-50'}
                isDark={isDark}
              />
              <StatCard
                icon={<FaKey className="text-4xl text-orange-600" />}
                title="API Keys فعال"
                value={`${stats.active_api_keys} / ${stats.total_api_keys}`}
                bgColor={isDark ? 'bg-orange-900/20' : 'bg-orange-50'}
                isDark={isDark}
              />
            </div>
          )}

          {activeTab === 'api-keys' && (
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-6 transition-colors duration-300`}>
              <div className="flex justify-between items-center mb-6">
                <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  مدیریت API Keys
                </h3>
                <button
                  onClick={() => setShowAddKeyModal(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-colors"
                >
                  <FaPlus /> افزودن کلید جدید
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className={isDark ? 'bg-gray-900' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>نام</th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>کلید</th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>وضعیت</th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>آخرین استفاده</th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>عملیات</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {apiKeys.map((key) => (
                      <tr key={key.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                          {key.name}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-mono ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {key.key.substring(0, 20)}...
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            key.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {key.status === 'active' ? 'فعال' : 'غیرفعال'}
                          </span>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {key.last_used ? formatDate(key.last_used) : 'هرگز'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleDeleteKey(key.id)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'subscriptions' && (
            <div className="space-y-6">
              {subscriptions.map((sub) => (
                <div key={sub.id} className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-6 transition-colors duration-300`}>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className={`font-bold text-lg mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        {sub.user_info?.first_name} {sub.user_info?.last_name}
                      </h4>
                      <div className={`space-y-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        <p>📱 {sub.user_info?.mobile}</p>
                        <p>🔖 نوع: {sub.subscription_type === 'weekly' ? 'یک هفته‌ای' : 'یک ماهه'}</p>
                        <p>📅 تاریخ: {formatDate(sub.requested_at)}</p>
                        <p>
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            sub.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : sub.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {sub.status === 'pending' ? 'در انتظار' : sub.status === 'approved' ? 'تایید شده' : 'رد شده'}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div>
                      {sub.receipt_file && (
                        <div className="mb-4">
                          <p className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>رسید:</p>
                          <img
                            src={sub.receipt_file}
                            alt="Receipt"
                            className="w-full h-48 object-contain border rounded-lg"
                          />
                        </div>
                      )}

                      {sub.status === 'pending' && (
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleApproveSubscription(sub.id)}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors"
                          >
                            <FaCheck /> تایید
                          </button>
                          <button
                            onClick={() => handleRejectSubscription(sub.id)}
                            className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors"
                          >
                            <FaTimes /> رد
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {subscriptions.length === 0 && (
                <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  هیچ درخواستی موجود نیست
                </div>
              )}
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-6 transition-colors duration-300`}>
              <div className="mb-6">
                <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  📢 ارسال نوتیفیکیشن
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  این نوتیفیکیشن به تمام کاربرانی که اپلیکیشن را نصب کرده و دسترسی نوتیفیکیشن داده‌اند ارسال می‌شود
                </p>
              </div>

              {/* Stats Card */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mb-6 p-4 rounded-xl ${
                  isDark ? 'bg-gradient-to-r from-green-900/30 to-blue-900/30 border border-green-700/50' : 'bg-gradient-to-r from-green-50 to-blue-50 border border-green-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="text-3xl">👥</div>
                  <div>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>کاربران فعال</p>
                    <p className={`text-2xl font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {pushSubscriptionsCount.active} <span className="text-sm font-normal">نفر</span>
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Notification Form */}
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    عنوان نوتیفیکیشن <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={notificationData.title}
                    onChange={(e) => setNotificationData({ ...notificationData, title: e.target.value })}
                    placeholder="مثلاً: خبر مهم از نوآفرین‌شو"
                    maxLength={100}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 transition-all ${
                      isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {notificationData.title.length} / 100 کاراکتر
                  </p>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    متن نوتیفیکیشن <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={notificationData.body}
                    onChange={(e) => setNotificationData({ ...notificationData, body: e.target.value })}
                    placeholder="متن پیام خود را اینجا بنویسید..."
                    maxLength={200}
                    rows={4}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 transition-all resize-none ${
                      isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {notificationData.body.length} / 200 کاراکتر
                  </p>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    لینک (اختیاری)
                  </label>
                  <input
                    type="text"
                    value={notificationData.url}
                    onChange={(e) => setNotificationData({ ...notificationData, url: e.target.value })}
                    placeholder="/novayar"
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 transition-all ${
                      isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    هنگام کلیک روی نوتیفیکیشن، به این صفحه هدایت می‌شود
                  </p>
                </div>

                {/* Send Button */}
                <motion.button
                  onClick={handleSendNotification}
                  disabled={sendingNotification || !notificationData.title || !notificationData.body}
                  whileHover={{ scale: sendingNotification ? 1 : 1.02 }}
                  whileTap={{ scale: sendingNotification ? 1 : 0.98 }}
                  className={`w-full py-4 rounded-xl font-bold text-lg shadow-xl transition-all flex items-center justify-center gap-3 ${
                    sendingNotification || !notificationData.title || !notificationData.body
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white hover:shadow-2xl'
                  }`}
                >
                  {sendingNotification ? (
                    <>
                      <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                      در حال ارسال...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane />
                      ارسال نوتیفیکیشن به {pushSubscriptionsCount.active} کاربر
                    </>
                  )}
                </motion.button>

                {/* Preview Card */}
                {(notificationData.title || notificationData.body) && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mt-6 p-4 rounded-xl border ${
                      isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <p className={`text-xs font-bold mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      👁️ پیش‌نمایش نوتیفیکیشن:
                    </p>
                    <div className={`p-3 rounded-lg shadow-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                      <div className="flex items-start gap-3">
                        <img 
                          src="https://i.postimg.cc/G2CfddsL/Picsart-25-11-12-16-56-18-294.jpg" 
                          alt="icon" 
                          className="w-10 h-10 rounded-lg"
                        />
                        <div className="flex-1">
                          <p className={`font-bold text-sm mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {notificationData.title || 'عنوان نوتیفیکیشن'}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {notificationData.body || 'متن نوتیفیکیشن در اینجا نمایش داده می‌شود'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Add Key Modal */}
      {showAddKeyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 max-w-md w-full transition-colors duration-300`}>
            <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>افزودن API Key</h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  نام (اختیاری)
                </label>
                <input
                  type="text"
                  value={newKeyData.name}
                  onChange={(e) => setNewKeyData({ ...newKeyData, name: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="Key 1"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  API Key <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newKeyData.key}
                  onChange={(e) => setNewKeyData({ ...newKeyData, key: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg font-mono text-sm ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  rows="3"
                  placeholder="sk-..."
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleAddKey}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold transition-colors"
                >
                  افزودن
                </button>
                <button
                  onClick={() => setShowAddKeyModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-bold transition-colors"
                >
                  انصراف
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon, title, value, bgColor, isDark }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className={`${bgColor} rounded-2xl p-6 shadow-lg transition-all duration-300`}
  >
    <div className="flex items-center justify-between mb-3">
      {icon}
    </div>
    <h4 className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{title}</h4>
    <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</p>
  </motion.div>
);

export default NovayarAdmin;
